import React, { useState } from 'react'
import { DateTime } from 'luxon'

import { Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import { DATE_FORMATS_LUXON, isoToUTCDateTime } from '@/utils/dateHelpers'

import {
  isSentinelAnswer,
  QUARTERLY_TAX_INCOME_BANDS,
  SECTION_INTROS,
  SECTION_LABELS,
  TAX_TREATMENTS,
} from '../lib/constants'
import type { ComplianceLessonKey, IntakeAnswers } from '../lib/types'
import {
  everyStateAnswered,
  isAnswered,
  isSectionComplete,
  visibleQuestions,
} from '../questions/registry'
import type { QuestionDef } from '../questions/types'
import { QuestionRow } from './QuestionRow'
import {
  SectionSummaryCard,
  SectionSummaryCardProps,
} from './SectionSummaryCard'
import { useAnimateRevealedQuestions } from './useAnimateRevealedQuestions'
import { useMotionGuard } from './useMotionGuard'
import { useScrollIntoViewOnReady } from './useScrollIntoViewOnReady'
import { useClearOnOutsideInteraction } from './useClearOnOutsideInteraction'

/** Reads as "answered" long before the user is done — a slider on its first
 *  drag tick, a text field on its first keystroke. These wait for the user to
 *  move on instead. */
const SETTLES_ON_MOVING_ON_INPUTS = new Set<QuestionDef['input']>([
  'number',
  'income_band_slider',
  'text',
  'tel',
  'multi',
  'states',
])

/** A dropdown closes itself on selection, so nothing is left in flight. */
const COMMITS_ON_SELECTION_INPUTS = new Set<QuestionDef['input']>([
  'date',
  'state',
])

/** `states` prepopulates from an earlier answer, so it arrives holding a value
 *  the user never chose. Confirm is the only signal they've looked. */
const REQUIRES_CONFIRMATION_INPUTS = new Set<QuestionDef['input']>(['states'])

export const requiresConfirmation = (def: QuestionDef): boolean =>
  REQUIRES_CONFIRMATION_INPUTS.has(def.input)

/**
 * Whether a month select is still on screen, so a year alone hasn't finished
 * the question and it waits rather than closing.
 *
 * Both date fields write the month as `?? 1`, making a year-only value
 * indistinguishable from an explicit January, so this asks the fields instead:
 * `YearMonthDateField` always shows a month select, `RecentPastDateField` only
 * for the current year. An explicit skip stores one of the two sentinels (or
 * `null`, for the field that offers no skip) and shows none.
 */
export const awaitingMonth = (def: QuestionDef, value: unknown): boolean => {
  if (
    def.input !== 'date' ||
    value === null ||
    value === undefined ||
    isSentinelAnswer(value)
  ) {
    return false
  }
  if (!def.recentPastDate) return true
  // Parsed as UTC because the stored value is a plain calendar date, but
  // compared against the local year because that is the set of years the
  // field offered. An unparsable value has not finished either, so it holds
  // the question open rather than collapsing as though it were answered.
  const stored = isoToUTCDateTime(String(value))
  return !stored.isValid || stored.year === DateTime.local().year
}

/** Every per-state row uses the same year+month picker as a plain `date`, so
 *  a row holding a real date has its month select still on screen and hasn't
 *  finished, same rule as `awaitingMonth`. A skipped row shows no month, so it
 *  doesn't hold the question open. */
const awaitingMonthInAnyState = (value: unknown): boolean =>
  Object.values((value ?? {}) as Record<string, unknown>).some(
    (v) => v !== null && v !== undefined && !isSentinelAnswer(v)
  )

/** Whether a question is done enough to collapse. For an optional question a
 *  skip (`null`) counts, `undefined` doesn't; past that it depends on the
 *  input — see the sets above. */
const isResolved = (
  def: QuestionDef,
  value: unknown,
  answers: IntakeAnswers,
  activeKey: string | undefined,
  confirmedKeys: Set<string>
): boolean => {
  const answered = def.optional ? value !== undefined : isAnswered(value)
  if (!answered) return false
  if (def.input === 'per_state_date' || def.input === 'per_state_select') {
    if (!everyStateAnswered(value, answers)) return false
    // A per-state date is a stack of the same year+month pickers a plain
    // `date` uses, so it settles the same way: a row holding a real date still
    // has a month to pick, and closing on the year would submit the write-time
    // January. Waits for the user to move on rather than closing under them.
    return def.input === 'per_state_select' || !awaitingMonthInAnyState(value)
      ? true
      : activeKey !== def.key
  }
  if (REQUIRES_CONFIRMATION_INPUTS.has(def.input)) {
    return confirmedKeys.has(def.key)
  }
  if (COMMITS_ON_SELECTION_INPUTS.has(def.input)) {
    // Finished on selection, unless a second choice is still on screen — then
    // it waits like the settles-on-moving-on inputs do.
    return !awaitingMonth(def, value) || activeKey !== def.key
  }
  return SETTLES_ON_MOVING_ON_INPUTS.has(def.input)
    ? activeKey !== def.key
    : true
}

/** A real IRS deadline from `quarterly_tax_estimate_details`. */
export interface QuarterlyDueDate {
  quarter: string
  dueAt: DateTime
}

interface IntakeSectionProps {
  section: ComplianceLessonKey
  index: number
  total: number
  answers: IntakeAnswers
  onAnswer: (key: string, value: unknown) => void
  /** True once the user has tried to continue past this section while it was incomplete. */
  showErrors?: boolean
  /** Real upcoming IRS deadlines, soonest first. Empty while they load, or if
   *  the table holds nothing future — the card copes with either. */
  quarterlyDueDates?: QuarterlyDueDate[]
}

/**
 * Figures from Heard's own therapist survey. Named so the number and the
 * sentence around it can be updated together, and so a reader can tell a
 * surveyed figure from a rhetorical one. Don't invent new ones.
 */
const SURVEY = {
  scorpHeadache: '31.1%',
  scorpConsidering: '25%',
  insuranceCostly: '36.6%',
  billingViaPlatform: '54.7%',
  billingThemselves: '29.2%',
  billingDedicated: '16.1%',
} as const

/** The federal test, shared by the static and live cards so the two
 *  near-identical sentences cannot drift apart. */
const OWED_TAX_RULE = 'you expect to owe $1,000 or more in tax when you file'

// Closing note for each section. Static educational copy — there's no
// per-section coverage computation behind these, so they teach something
// about the ground just covered rather than claiming to have assessed it.
const SECTION_SUMMARY: Record<ComplianceLessonKey, SectionSummaryCardProps> = {
  license: {
    eyebrow: 'Did you know',
    headline: 'Every state renews your license on its own clock',
    description:
      'Renewal cycles, CEU requirements, and filing deadlines all differ by state, so holding licenses in more than one means keeping more than one calendar.',
  },
  // Nothing else in the section names quarterly taxes, so this is where the
  // live variant below gets its groundwork.
  //
  // Deliberately non-conclusive for the bands below the quarterly-tax set: the
  // bands are gross practice income and the federal test turns on tax owed, so
  // no band boundary settles applicability. The card names the rule and lets
  // the reader place themselves against it.
  practice_stage: {
    eyebrow: 'Did you know',
    headline: 'Quarterly taxes are worth understanding early',
    description: `A paycheck has tax withheld for you. Practice income doesn't, so past a certain point the IRS expects tax four times a year rather than once at filing. That generally starts once ${OWED_TAX_RULE}, so it is worth checking where you land.`,
  },
  entity: {
    eyebrow: 'Did you know',
    headline:
      'Whether to elect S corporation is one of the most common questions therapists have',
    description: (
      <>
        <b>{SURVEY.scorpHeadache}</b> of therapists name &lsquo;knowing when or
        if to elect S corporation&rsquo; a top-three business headache, and{' '}
        <b>{SURVEY.scorpConsidering}</b> are actively considering it this year.
        This is something Heard helps with year-round.
      </>
    ),
  },
  insurance: {
    eyebrow: 'Did you know',
    headline: 'Insurance is one of the biggest line items in a practice',
    description: (
      <>
        <b>{SURVEY.insuranceCostly}</b> of therapists name insurance
        (malpractice, health, and benefits) among their three most costly
        business expenses. While important, it is a cost to be aware of.
      </>
    ),
  },
  getting_paid: {
    eyebrow: 'Did you know',
    headline: "Most therapists don't handle their own billing",
    description: (
      <>
        Only <b>{SURVEY.billingThemselves}</b> of insurance-accepting therapists
        do their own billing, and <b>{SURVEY.billingDedicated}</b> work with a
        dedicated biller. The rest, <b>{SURVEY.billingViaPlatform}</b>, let a
        platform handle it.
      </>
    ),
  },
  money_books: {
    eyebrow: 'Did you know',
    headline: 'Clean books start with separating business and personal money',
    description:
      'A dedicated business account is the single change that makes bookkeeping, taxes, and deductions all easier at once, before any software enters the picture.',
  },
}

/** Two, so the card reads as a cadence rather than a one-off. Exported for
 *  the intake page, which does the fetching. */
export const QUARTERLY_DUE_DATES_SHOWN = 2

/**
 * In a band that carries the obligation, the card names it and its next
 * deadlines; otherwise the static card stands.
 *
 * No variant states applicability as settled, and the live one says "likely". The bands are
 * gross practice income while the federal test turns on expecting $1,000+ of tax owed, so no
 * boundary maps cleanly onto the trigger. In the higher bands the band is a good enough proxy
 * to name the obligation; below them the static card names the rule and leaves the reader to
 * place themselves against it.
 *
 * Deadlines come from `quarterly_tax_estimate_details` via the page, not from
 * nominal 15ths computed here: `irsPaymentDueAt` already carries the IRS shift
 * to the next business day. When the table has nothing upcoming the card still
 * renders, minus the dates and the sentence that would have introduced them.
 *
 * Two things that look like copy choices but aren't. The headline speaks of
 * income, not profit, because income is the only one of the two this intake
 * collects. And it softens when `client_status` isn't 'yes', because the
 * report's `pay_estimated_taxes` obligation is NOT_RELEVANT until then
 * (`obligations/practice_stage.ts`) — asserting it here would contradict the
 * report two screens later.
 */
export const practiceStageSummaryForTest = (
  answers: IntakeAnswers,
  quarterlyDueDates?: QuarterlyDueDate[]
): SectionSummaryCardProps => practiceStageSummary(answers, quarterlyDueDates)

const practiceStageSummary = (
  answers: IntakeAnswers,
  quarterlyDueDates: QuarterlyDueDate[] = []
): SectionSummaryCardProps => {
  const incomeBand = answers.expected_income_band as string | undefined
  if (!incomeBand || !QUARTERLY_TAX_INCOME_BANDS.has(incomeBand)) {
    return SECTION_SUMMARY.practice_stage
  }
  const seeingClients = answers.client_status === 'yes'
  const dueDates = quarterlyDueDates.map(({ quarter, dueAt }) => ({
    label: `Q${quarter} estimated tax payment`,
    date: dueAt.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT_NO_PAD),
  }))
  // Only promise deadlines the card can actually show.
  const deadlines = dueDates.length > 0
  // Same rule either way; only what it means for them changes.
  const rule = `Quarterly estimated tax generally starts once ${OWED_TAX_RULE}.`
  // An FYI, not a task label: the card flags this for awareness, not action.
  const eyebrow = 'Just so you know'
  return seeingClients
    ? {
        eyebrow,
        headline:
          'At the income you expect, quarterly taxes likely apply to you',
        dueDates,
        description: deadlines
          ? `${rule} These are your next deadlines.`
          : rule,
      }
    : {
        eyebrow,
        headline: 'Once you start seeing clients, quarterly taxes may apply',
        dueDates,
        description: deadlines
          ? `${rule} That clock starts with your first practice income, so this is the schedule you would be on.`
          : `${rule} That clock starts with your first practice income.`,
      }
}

export const entitySummaryForTest = (
  answers: IntakeAnswers
): SectionSummaryCardProps => entitySummary(answers)

/**
 * A practice that has been an S corporation for years does not need to be asked
 * whether to elect one. Names what the election actually obliges instead, both
 * of which are already obligations in the report.
 */
const entitySummary = (answers: IntakeAnswers): SectionSummaryCardProps => {
  if (answers.tax_treatment !== TAX_TREATMENTS.scorp) {
    return SECTION_SUMMARY.entity
  }
  return {
    eyebrow: 'Just so you know',
    headline: 'An S corporation brings payroll and a reasonable salary with it',
    description:
      'Electing S corporation means paying yourself a salary through payroll before taking any profit. The IRS expects that salary to be roughly what your job pays in your area, and taking only distributions is what gets reclassified later.',
  }
}

const LIVE_SUMMARIES: Partial<
  Record<
    ComplianceLessonKey,
    (
      answers: IntakeAnswers,
      quarterlyDueDates?: QuarterlyDueDate[]
    ) => SectionSummaryCardProps
  >
> = {
  practice_stage: practiceStageSummary,
  entity: entitySummary,
}

export const IntakeSection: React.FC<IntakeSectionProps> = ({
  section,
  index,
  total,
  answers,
  onAnswer,
  showErrors,
  quarterlyDueDates,
}) => {
  const questions = visibleQuestions(section, answers)
  const isComplete = isSectionComplete(section, answers)
  const summary =
    LIVE_SUMMARIES[section]?.(answers, quarterlyDueDates) ??
    SECTION_SUMMARY[section]
  const summaryRef = useScrollIntoViewOnReady(isComplete)
  useAnimateRevealedQuestions(section, questions)
  // Questions re-opened for editing after they'd already collapsed —
  // everything else collapses automatically once resolved. Keyed by
  // question key (unique across all sections), so stale entries from a
  // section the user already left are harmless no-ops here.
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
  // The question the user is currently working on. Same keying as
  // `expandedKeys`, and stale for the same harmless reason: a key left over
  // from a section the user has moved past matches nothing here.
  const [activeKey, setActiveKey] = useState<string | undefined>()
  // Answers arrived with are already settled; ones made this session need
  // confirming. Seeded at mount, so a returning user's completed questions
  // render rolled up. `IntakeSection` is keyed by section so this is per
  // section, not per visit.
  const [confirmedKeys, setConfirmedKeys] = useState<Set<string>>(
    () => new Set(Object.keys(answers).filter((k) => isAnswered(answers[k])))
  )
  const toggleExpanded = (key: string): void => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
    // "Done editing" has to end the working-on-it state as well, or a question
    // that only settles once you move on would refuse the very collapse the
    // user just asked for.
    setActiveKey((prev) => (prev === key ? undefined : prev))
  }
  // Pressing anywhere outside the question ends editing too — without this,
  // the last question you touch stays open until you happen to click another
  // one, which never comes for the last question in a section.
  useClearOnOutsideInteraction(activeKey, () => setActiveKey(undefined))
  /** Drops both marks that hold a question open: working-on-it, and
   *  reopened-it. Clearing only the first left a finished question open for
   *  anyone who'd reopened it. */
  const releaseQuestion = (key: string): void => {
    setActiveKey((prev) => (prev === key ? undefined : prev))
    setExpandedKeys((prev) => {
      if (!prev.has(key)) return prev
      const next = new Set(prev)
      next.delete(key)
      return next
    })
  }
  /** Marks a question finished and closes it up. */
  const confirmQuestion = (key: string): void => {
    setConfirmedKeys((prev) => (prev.has(key) ? prev : new Set(prev).add(key)))
    releaseQuestion(key)
  }
  const handleAnswer = (key: string, value: unknown): void => {
    onAnswer(key, value)
    // Two answers that are finished the moment they land: an explicit skip
    // ("Skip, I don't know"), and a pick from a dropdown. Releasing here as
    // well as in `isResolved` is what closes one the user had reopened.
    const def = questions.find((q) => q.key === key)
    // Finished the moment it lands: an explicit skip, a dropdown pick, or an
    // option card. The first two were already here; option cards are the most
    // common input and behaved differently once reopened — the row stayed open
    // after a new pick because `expandedKeys` still held it.
    //
    // A per-state skip needs its own arm: it writes into one row of a map
    // rather than replacing the whole value, so the scalar checks above never
    // see it and a reopened question stayed open with its field cleared. Every
    // row skipped means there is no month left to pick.
    const settled =
      value === null ||
      isSentinelAnswer(value) ||
      (def !== undefined &&
        ((def.input === 'per_state_date' &&
          everyStateAnswered(value, answers) &&
          !awaitingMonthInAnyState(value)) ||
          (isAnswered(value) &&
            (def.input === 'single' ||
              (COMMITS_ON_SELECTION_INPUTS.has(def.input) &&
                !awaitingMonth(def, value))))))
    if (settled) confirmQuestion(key)
  }
  const rows = questions.map((q) => {
    const value = answers[q.key]
    const resolved = isResolved(q, value, answers, activeKey, confirmedKeys)
    const reopened = expandedKeys.has(q.key)
    return { q, value, isCollapsed: resolved && !reopened, reopened }
  })
  const isSettling = useMotionGuard(
    rows.map((r) => (r.isCollapsed ? '1' : '0')).join('')
  )
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
          {`Section ${index + 1} of ${total}`}
        </Text>
        <Text variant={TypographyStyles.DISPLAY_SM} color={TextColors.HEADER}>
          {SECTION_LABELS[section]}
        </Text>
        <Text variant={TypographyStyles.BODY_MD} color={TextColors.MUTED}>
          {SECTION_INTROS[section]}
        </Text>
      </div>
      {/* Tighter than the section's own rhythm above: each question now
          carries its own border, so it no longer needs whitespace to read as
          a separate thing. */}
      {/* Deaf to the pointer while the list is still moving: a click landing
          mid-slide picks whichever control slid under the cursor, which here
          records an answer the user never chose. Pointer events only, so
          keyboard answering is unaffected. */}
      <div
        data-motion-guard={isSettling || undefined}
        className={mtc(
          'intake-question-list flex flex-col gap-4',
          isSettling && 'pointer-events-none'
        )}
      >
        {rows.map(({ q, value, isCollapsed, reopened }) => {
          // Present for every question that has to be confirmed, answered or
          // not: a control that appears only once you've filled something in
          // gives no hint that confirming is what finishes the question.
          const needsConfirming = requiresConfirmation(q)
          return (
            <QuestionRow
              key={q.key}
              def={q}
              value={value}
              allAnswers={answers}
              isCollapsed={isCollapsed}
              isReopened={reopened}
              onToggle={() => toggleExpanded(q.key)}
              onAnswer={handleAnswer}
              onInteract={setActiveKey}
              onConfirm={
                needsConfirming ? () => confirmQuestion(q.key) : undefined
              }
              confirmDisabled={!isAnswered(value)}
              hasError={
                Boolean(showErrors) &&
                !q.optional &&
                !isAnswered(answers[q.key])
              }
            />
          )
        })}
      </div>
      {isComplete && (
        <div ref={summaryRef}>
          <SectionSummaryCard {...summary} />
        </div>
      )}
    </div>
  )
}
